import React from 'react';
import { Link } from 'react-router-dom';
import { Shield, Truck, PlayCircle, ArrowRight, Check } from 'lucide-react';
import { Button } from '../components/ui/button';

const HomePage = () => {
  const features = [
    {
      icon: Shield,
      title: '10 Year Warranty',
      description: 'Premium Vetrox film backed by our industry-leading warranty'
    },
    {
      icon: Truck,
      title: 'Australia-Wide Delivery',
      description: 'Flat $35 shipping via Australia Post to your door'
    },
    {
      icon: PlayCircle,
      title: 'Tutorial Videos',
      description: 'Step-by-step installation guides included with every kit'
    }
  ];

  const products = [
    {
      name: 'Whole Body Gloss',
      description: 'Crystal clear protection',
      price: 'From $2,400',
      image: 'https://images.pexels.com/photos/14231700/pexels-photo-14231700.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940'
    },
    {
      name: 'Whole Body Matt',
      description: 'Sleek satin finish',
      price: 'From $2,700',
      image: 'https://images.pexels.com/photos/32726112/pexels-photo-32726112.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940'
    },
    {
      name: 'Whole Body Coloured',
      description: 'Transform your vehicle',
      price: 'From $3,100',
      image: 'https://images.pexels.com/photos/10162529/pexels-photo-10162529.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940'
    },
    {
      name: 'Essential Package',
      description: 'High-impact areas',
      price: 'From $550',
      image: 'https://images.pexels.com/photos/10126665/pexels-photo-10126665.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940'
    }
  ];

  const kitIncludes = [
    'Custom cut Vetrox PPF film for your specific vehicle',
    'Installation gel for bubble-free application',
    'Pressure spray bottle',
    'Application spray bottle',
    'Professional cutting blade',
    'High-quality squeegee',
    'Comprehensive tutorial videos'
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative min-h-[90vh] flex items-center">
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{ backgroundImage: `url('https://images.pexels.com/photos/14231700/pexels-photo-14231700.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1200&w=1920')` }}
        />
        <div className="absolute inset-0 hero-overlay" />
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32">
          <div className="max-w-2xl">
            <p className="text-xs uppercase tracking-[0.2em] font-bold text-[#00F0FF] mb-4 animate-fade-in">
              Premium DIY Paint Protection
            </p>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl tracking-tighter font-light text-[#F8F8F8] mb-6 animate-fade-in animate-delay-100">
              Protect Your Investment.<br />
              <span className="font-medium">Do It Yourself.</span>
            </h1>
            <p className="text-lg text-[#A0A0A0] leading-relaxed mb-8 animate-fade-in animate-delay-200">
              Premium Vetrox PPF film custom-cut for your vehicle. Professional quality paint protection with everything you need for DIY installation.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 animate-fade-in animate-delay-300">
              <Link to="/products">
                <Button 
                  className="bg-[#00F0FF] text-[#050505] font-bold uppercase tracking-wide hover:bg-[#33F3FF] px-8 py-6 rounded-none text-base"
                  data-testid="hero-build-kit-btn"
                >
                  Build Your Kit
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
              <Link to="/warranty">
                <Button 
                  variant="outline"
                  className="border-[#444444] text-[#F8F8F8] hover:bg-[#111111] hover:border-[#F8F8F8] px-8 py-6 rounded-none text-base"
                  data-testid="hero-warranty-btn"
                >
                  10 Year Warranty
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-24 bg-[#050505]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <div 
                key={feature.title}
                className="bg-[#111111] border border-[#222222] p-8 transition-all duration-300 hover:border-[#444444]"
                data-testid={`feature-card-${index}`}
              >
                <div className="w-12 h-12 bg-[#1A1A1A] border border-[#222222] flex items-center justify-center mb-6">
                  <feature.icon className="w-6 h-6 text-[#00F0FF]" />
                </div>
                <h3 className="text-xl font-medium text-[#F8F8F8] mb-3">{feature.title}</h3>
                <p className="text-[#A0A0A0] leading-relaxed">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Products Preview Section */}
      <section className="py-24 bg-[#0A0A0A]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="text-xs uppercase tracking-[0.2em] font-bold text-[#00F0FF] mb-4">Our Products</p>
            <h2 className="text-3xl sm:text-4xl tracking-tight font-medium text-[#F8F8F8]">
              Choose Your Protection Level
            </h2>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {products.map((product, index) => (
              <Link 
                to="/products" 
                key={product.name}
                className="group"
                data-testid={`product-preview-${index}`}
              >
                <div className="bg-[#111111] border border-[#222222] overflow-hidden transition-all duration-300 group-hover:border-[#444444]">
                  <div className="aspect-[4/3] relative overflow-hidden">
                    <img 
                      src={product.image} 
                      alt={product.name}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#050505] to-transparent opacity-60" />
                  </div>
                  <div className="p-6">
                    <h3 className="text-lg font-medium text-[#F8F8F8] mb-1">{product.name}</h3>
                    <p className="text-[#666666] text-sm mb-3">{product.description}</p>
                    <p className="text-[#00F0FF] font-bold">{product.price}</p>
                  </div>
                </div>
              </Link>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link to="/products">
              <Button 
                className="bg-[#00F0FF] text-[#050505] font-bold uppercase tracking-wide hover:bg-[#33F3FF] px-8 py-4 rounded-none"
                data-testid="view-all-products-btn"
              >
                View All Products
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* What's Included Section */}
      <section className="py-24 bg-[#050505]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <p className="text-xs uppercase tracking-[0.2em] font-bold text-[#00F0FF] mb-4">Complete Kit</p>
              <h2 className="text-3xl sm:text-4xl tracking-tight font-medium text-[#F8F8F8] mb-6">
                Everything You Need
              </h2>
              <p className="text-[#A0A0A0] leading-relaxed mb-8">
                Every DIY PPF Kit comes complete with professional-grade tools and materials. No additional purchases required.
              </p>
              <ul className="space-y-4">
                {kitIncludes.map((item, index) => (
                  <li key={index} className="flex items-start space-x-3">
                    <div className="w-5 h-5 bg-[#00F0FF] rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Check className="w-3 h-3 text-[#050505]" />
                    </div>
                    <span className="text-[#F8F8F8]">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="relative">
              <img 
                src="https://images.pexels.com/photos/31154210/pexels-photo-31154210.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                alt="Installation tools"
                className="w-full border border-[#222222]"
              />
              <div className="absolute -bottom-6 -left-6 bg-[#00F0FF] text-[#050505] px-6 py-3 font-bold">
                Optional Kit +$100
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-[#111111] border-y border-[#222222]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl tracking-tight font-medium text-[#F8F8F8] mb-6">
            Ready to Protect Your Vehicle?
          </h2>
          <p className="text-[#A0A0A0] text-lg mb-8 max-w-2xl mx-auto">
            Select your vehicle and choose your protection level. Custom-cut film delivered to your door with everything you need for professional installation.
          </p>
          <Link to="/products">
            <Button 
              className="bg-[#00F0FF] text-[#050505] font-bold uppercase tracking-wide hover:bg-[#33F3FF] px-10 py-6 rounded-none text-lg"
              data-testid="cta-build-kit-btn"
            >
              Build Your Kit Now
              <ArrowRight className="ml-2 w-6 h-6" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
